import {ReactElement, useContext, useEffect, useState} from "react";
import {CSVBoxButton} from "../../../../../components/csvbox/CSVBoxButton";
import {csvBoxExtractRows, csvBoxGeneralError, normalizeDateHeaders} from "../../../../../global/csv_box_helpers";
import {Button} from "@zendeskgarden/react-buttons";
import {AuthContext} from "../../../../../contexts/AuthContext";
import {
    useGenerateAcquisitionAccountValuesFileUploadUrlMutation,
    useProcessAcquisitionAccountValuesFileMutation
} from "../../../../../__generated__/generated_types";
import {toast} from "react-toastify";

function csvBoxHasError(result: boolean, data: unknown): boolean {
    if(!result) {
        return true;
    }

    if(typeof data === "object" && data !== null) {
        const tmp = data as Record<string, unknown>;
        // External validation flag.
        const evFailed = tmp["ev_failed"];
        if(typeof evFailed === "number" && evFailed === 1) {
            return true;
        }
    }
    return false;
}

interface DirectCoAActualsDataItem {
    accountName: string;
    accountNumber: string;
    [k: string]: string;
}

function extractData(values: unknown[]): DirectCoAActualsDataItem[] {
    const retVals: DirectCoAActualsDataItem[] = [];
    for(const value of values) {
        if(typeof value !== "object" || value === null) {
            continue;
        }

        const row = value as Record<string, unknown>;
        const unmappedData = row["_unmapped_data"] as Record<string, unknown> | undefined;

        if(typeof unmappedData !== "object" || unmappedData === null || unmappedData === undefined) {
            continue;
        }

        const accountName = row["Account Name"];
        const accountNumber = row["Account Number"];

        if(typeof accountName !== "string") {
            continue;
        }

        if(typeof accountNumber !== "string") {
            continue;
        }

        const normalizedDateHeaders: Record<string, string> = {};
        for(const [header, value] of Object.entries(unmappedData)) {
            const normalized = normalizeDateHeaders(header);
            if(normalized !== null) {
                normalizedDateHeaders[normalized] = String(value);
            }
        }

        retVals.push({
            accountName: accountName,
            accountNumber: accountNumber,
            ...normalizedDateHeaders
        });
    }

    return retVals;
}

export interface AcquisitionPropertyActualsButtonProps {
    propertyId: string;
    jobCreated: (jobId: string, propertyId: string) => void;
}

export function DirectCoAActualsButton(props: AcquisitionPropertyActualsButtonProps): ReactElement {
    const {user, budgetYear} = useContext(AuthContext);

    const [generateUrl, {loading: loadingUrl, data: urlData}] = useGenerateAcquisitionAccountValuesFileUploadUrlMutation({
        notifyOnNetworkStatusChange: true
    });

    const [processFile, {loading: loadingProcess}] = useProcessAcquisitionAccountValuesFileMutation({
        notifyOnNetworkStatusChange: true
    });

    const [data, setData] = useState<DirectCoAActualsDataItem[] | null>(null);

    useEffect(() => {
        if(loadingUrl || !urlData) {
            return;
        }

        doUpload();
    }, [urlData, loadingUrl]);

    function doUpload(): void {
        if(!data) {
            return;
        }

        if(!urlData) {
            toast.error("Unable to determine file location.");
            return;
        }

        // Get URLs and keys
        const accountValuesUrl = urlData.generateAcquisitionAccountValuesFileUploadUrl.signedUrl;
        const accountValuesKey = urlData.generateAcquisitionAccountValuesFileUploadUrl.key;

        const encoder = new TextEncoder();
        const encoded = encoder.encode(JSON.stringify(data));

        // Create array of upload promises
        fetch(accountValuesUrl, {
            method: 'PUT',
            body: encoded.buffer,
            headers: {
                'Content-Type': "application/json",
            }
        })
        .then(() => {
            return processFile({
                variables: {
                    propertyId: props.propertyId,
                    fileKey: accountValuesKey
                }
            });
        })
        .then(res => {
            if(res.data?.processAcquisitionAccountValuesFile) {
                props.jobCreated(res.data.processAcquisitionAccountValuesFile.id, props.propertyId);
                toast.success("Rent roll uploaded successfully.");
            }
        })
        .catch((error) => {
            console.error("Error uploading file:", error);
            toast.error("Error uploading file");
        });
    }

    function handleSave(data: unknown): void {
        const rows = csvBoxExtractRows(data);
        if(rows === undefined) {
            csvBoxGeneralError("account values");
            return;
        }

        const extracted = extractData(rows);
        if(extracted.length === 0) {
            csvBoxGeneralError("account values");
            return;
        }

        setData(extracted);
        generateUrl({
            variables: {
                propertyId: props.propertyId
            }
        });
    }

    return <CSVBoxButton
        licenseKey="vQmItM5wGldnQ1KQcajFr3fQqhaQ5k"
        user={{
            userId: user?.id ?? "unknown",
            name: user?.firstName || user?.lastName ? `${user?.firstName || ""} ${user?.lastName || ""}` : "unknown",
            email: user?.email ?? "unknown",
            clientId: user?.clientId ?? "unknown",
            clientName: user?.clientName ?? "unknown",
        }}
        environment={{
            // This gets set based on the environment to facilitate validation callbacks.
            "base_url": process.env["REACT_APP_CSV_BOX_BASE_URL"],
            "budget_year": budgetYear
        }}
        onImport={(result, data) => {
            if(!csvBoxHasError(result, data)) {
                handleSave(data);
            } else {
                csvBoxGeneralError("account values");
            }
        }}
        render={(launch, isLoading) => {
            return <Button onClick={launch} disabled={isLoading || loadingUrl || loadingProcess}>Use Property Chart of Accounts</Button>;
        }}
    />;
}